import {
    Autocomplete as MuiAutocomplete,
    AutocompleteProps as MuiAutocompleteProps,
    ChipTypeMap,
    InputAdornment,
    Paper,
    TextField,
    createFilterOptions,
    styled,
} from '@mui/material'
import type { AutocompleteValue as MuiAutocompleteValue } from '@mui/material/useAutocomplete'
import { CaretDownIcon, XIcon } from '@phosphor-icons/react'
import { tokens } from 'ComponentLibrary/style-dictionary/output'
import { ElementType, ForwardedRef, ReactElement, ReactNode, RefAttributes, forwardRef } from 'react'
import { Control, FieldPath, FieldValues, UseControllerProps, useController } from 'react-hook-form'
import type { DropdownOption } from '../Dropdown/Dropdown'
import { isRequiredRule } from '../TextInput/TextInput'

export type AutocompleteProps<
    Value,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
> = Omit<
    MuiAutocompleteProps<DropdownOption<Value>, Multiple, DisableClearable, FreeSolo, ChipComponent>,
    | 'defaultValue'
    | 'getOptionDisabled'
    | 'getOptionLabel'
    | 'isOptionEqualToValue'
    | 'onChange'
    | 'options'
    | 'ref'
    | 'renderInput'
    | 'value'
> & {
    'aria-label'?: string
    'aria-labelledby'?: string
    autoFocus?: boolean
    defaultValue?: AutocompleteValue<Value, Multiple, FreeSolo>
    endAdornment?: ReactNode
    errorMessage?: string
    isValueEqual?: (optionValue: Value, selectedValue: Value) => boolean
    label?: string
    name?: string
    onValueChange?: (value: AutocompleteValue<Value, Multiple, FreeSolo>) => void
    options: DropdownOption<Value>[]
    placeholder?: string
    required?: boolean
    value?: AutocompleteValue<Value, Multiple, FreeSolo>
}

type AutocompleteOptionValue<Value, FreeSolo extends boolean | undefined> = Value | (FreeSolo extends true ? string : never)

export type AutocompleteValue<
    Value,
    Multiple extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
> = Multiple extends true ? AutocompleteOptionValue<Value, FreeSolo>[] | undefined : AutocompleteOptionValue<Value, FreeSolo> | undefined

function AutocompleteComponent<
    Value,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
>(props: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>, ref: ForwardedRef<HTMLInputElement>) {
    const {
        'aria-label': ariaLabel,
        'aria-labelledby': ariaLabelledBy,
        autoFocus,
        autoSelect,
        className,
        clearIcon = <XIcon aria-hidden size={tokens.SizingIconS} weight="bold" />,
        defaultValue,
        disableCloseOnSelect,
        endAdornment,
        errorMessage,
        filterOptions = createFilterOptions<DropdownOption<Value>>(),
        freeSolo,
        fullWidth = true,
        isValueEqual = Object.is,
        label,
        multiple,
        name,
        onValueChange,
        options,
        placeholder,
        popupIcon = <CaretDownIcon aria-hidden size={tokens.SizingIconS} weight="bold" />,
        renderValue,
        required = false,
        slots,
        value,
        ...autocompleteProps
    } = props
    const isControlled = Object.prototype.hasOwnProperty.call(props, 'value')
    const resolveSelectedValue = (selectedValue: AutocompleteOptionValue<Value, FreeSolo>) => {
        const option = options.find(
            ({ value: optionValue }) =>
                (!freeSolo || typeof selectedValue !== 'string' || typeof optionValue === 'string') &&
                isValueEqual(optionValue, selectedValue as Value)
        )

        if (option) {
            return option
        }

        return freeSolo && typeof selectedValue === 'string' ? selectedValue : null
    }
    const resolveAutocompleteValue = (autocompleteValue: AutocompleteValue<Value, Multiple, FreeSolo> | undefined) => {
        if (multiple) {
            const selectedValues = (autocompleteValue ?? []) as AutocompleteOptionValue<Value, FreeSolo>[]
            return selectedValues
                .map(resolveSelectedValue)
                .filter((option): option is DropdownOption<Value> | (FreeSolo extends true ? string : never) => option !== null)
        }

        return autocompleteValue === undefined ? null : resolveSelectedValue(autocompleteValue as AutocompleteOptionValue<Value, FreeSolo>)
    }
    const resolvedValue = isControlled ? resolveAutocompleteValue(value) : undefined
    const resolvedDefaultValue = defaultValue === undefined ? undefined : resolveAutocompleteValue(defaultValue)

    return (
        <AutocompleteRoot className={className} data-multiple={multiple || undefined}>
            <MuiAutocomplete<DropdownOption<Value>, Multiple, DisableClearable, FreeSolo, ChipComponent>
                {...autocompleteProps}
                autoSelect={autoSelect}
                clearIcon={clearIcon}
                defaultValue={
                    resolvedDefaultValue as MuiAutocompleteValue<DropdownOption<Value>, Multiple, DisableClearable, FreeSolo> | undefined
                }
                disableCloseOnSelect={disableCloseOnSelect}
                filterOptions={(availableOptions, state) => {
                    const filteredOptions = filterOptions(availableOptions, state)
                    return freeSolo && multiple && autoSelect && disableCloseOnSelect && state.inputValue !== '' && filteredOptions.length === 0
                        ? [{ label: state.inputValue, value: state.inputValue as Value }]
                        : filteredOptions
                }}
                freeSolo={freeSolo}
                fullWidth={fullWidth}
                getOptionDisabled={(option) => !!option.disabled}
                getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
                isOptionEqualToValue={(option, selectedOption) =>
                    typeof selectedOption === 'string'
                        ? typeof option.value === 'string' && isValueEqual(option.value, selectedOption as Value)
                        : isValueEqual(option.value, selectedOption.value)
                }
                multiple={multiple}
                onChange={(_, selectedOption) => {
                    const toOptionValue = (option: DropdownOption<Value> | string) => (typeof option === 'string' ? option : option.value)
                    const nextValue = multiple
                        ? (selectedOption as (DropdownOption<Value> | string)[]).map(toOptionValue)
                        : selectedOption === null
                          ? undefined
                          : toOptionValue(selectedOption as DropdownOption<Value> | string)
                    onValueChange?.(nextValue as AutocompleteValue<Value, Multiple, FreeSolo>)
                }}
                options={options}
                popupIcon={popupIcon}
                renderValue={
                    renderValue && !multiple
                        ? (value, getItemProps, ownerState) => <SingleValue>{renderValue(value, getItemProps, ownerState)}</SingleValue>
                        : renderValue
                }
                slots={{
                    paper: AutocompletePaper,
                    ...slots,
                }}
                value={resolvedValue as MuiAutocompleteValue<DropdownOption<Value>, Multiple, DisableClearable, FreeSolo> | undefined}
                renderInput={(params) => {
                    const { inputProps, ...textFieldParams } = params

                    return (
                        <TextField
                            {...textFieldParams}
                            autoFocus={autoFocus}
                            error={errorMessage !== undefined}
                            helperText={errorMessage}
                            inputRef={ref}
                            label={label}
                            placeholder={placeholder}
                            required={required}
                            slotProps={{
                                input: {
                                    ...params.InputProps,
                                    endAdornment: endAdornment ? (
                                        <>
                                            <InputAdornment position="end">{endAdornment}</InputAdornment>
                                            {params.InputProps.endAdornment}
                                        </>
                                    ) : (
                                        params.InputProps.endAdornment
                                    ),
                                },
                                htmlInput: {
                                    ...inputProps,
                                    'aria-label': ariaLabel,
                                    'aria-labelledby': ariaLabelledBy,
                                    name,
                                },
                            }}
                            variant="outlined"
                        />
                    )
                }}
            />
        </AutocompleteRoot>
    )
}

export const Autocomplete = forwardRef(AutocompleteComponent) as <
    Value,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
>(
    props: AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent> & RefAttributes<HTMLInputElement>
) => ReactElement

type AutocompleteFieldPath<TFieldValues extends FieldValues> = FieldPath<TFieldValues>

type FormAutocompleteRules<TFieldValues extends FieldValues, TName extends AutocompleteFieldPath<TFieldValues>> = NonNullable<
    UseControllerProps<TFieldValues, TName>['rules']
>

export type FormAutocompleteProps<
    TFieldValues extends FieldValues,
    Value,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    TName extends AutocompleteFieldPath<TFieldValues> = AutocompleteFieldPath<TFieldValues>,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
> = Omit<
    AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
    'defaultValue' | 'errorMessage' | 'name' | 'required' | 'value'
> & {
    control: Control<TFieldValues>
    defaultValue?: UseControllerProps<TFieldValues, TName>['defaultValue']
    name: TName
    rules?: FormAutocompleteRules<TFieldValues, TName>
}

export function FormAutocomplete<
    TFieldValues extends FieldValues,
    Value,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    TName extends AutocompleteFieldPath<TFieldValues> = AutocompleteFieldPath<TFieldValues>,
    ChipComponent extends ElementType = ChipTypeMap['defaultComponent'],
>({
    control,
    defaultValue,
    multiple,
    name,
    onBlur,
    onValueChange,
    rules,
    ...autocompleteProps
}: FormAutocompleteProps<TFieldValues, Value, Multiple, DisableClearable, FreeSolo, TName, ChipComponent>) {
    const { field, fieldState } = useController({
        control,
        defaultValue,
        name,
        rules,
    })

    return (
        <Autocomplete<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>
            {...autocompleteProps}
            errorMessage={fieldState.error ? (fieldState.error.message?.toString() ?? '') : undefined}
            multiple={multiple}
            name={field.name}
            onBlur={(event) => {
                field.onBlur()
                onBlur?.(event)
            }}
            onValueChange={(newValue) => {
                field.onChange(newValue ?? undefined)
                onValueChange?.(newValue)
            }}
            ref={field.ref}
            required={isRequiredRule(rules?.required)}
            value={field.value}
        />
    )
}

const SingleValue = styled('span')({
    alignItems: 'center',
    display: 'flex',
    minWidth: 0,
    overflow: 'hidden',
    pointerEvents: 'none',
})

const AutocompleteRoot = styled('div')({
    minHeight: '46px',
    minWidth: 0,
    position: 'relative',
    width: '100%',
    '&[data-multiple="true"]': {
        height: 'auto',
        minHeight: '46px',
    },
    '& .MuiAutocomplete-root': {
        minWidth: 0,
        width: '100%',
    },
    '& .MuiInputLabel-root': {
        ...tokens.TextS,
        color: tokens.ColorTextSubtle,
        maxWidth: 'calc(100% - 48px)',
        transform: `translate(${tokens.SizingSpacing150}, 14px) scale(1)`,
        zIndex: 1,
        '&.Mui-focused': {
            color: tokens.ColorTextSubtle,
        },
        '&.Mui-error': {
            color: tokens.ColorTextDangerSubtle,
        },
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
        },
        '&.MuiInputLabel-shrink': {
            ...tokens.TextXs,
            backgroundColor: tokens.ColorSurfaceDefault,
            maxWidth: 'calc(100% - 24px)',
            padding: `0 ${tokens.SizingSpacing075}`,
            transform: `translate(${tokens.SizingSpacing075}, -7px) scale(1)`,
        },
        '&.MuiInputLabel-shrink.Mui-disabled': {
            backgroundColor: 'transparent',
        },
    },
    '&:has(.MuiOutlinedInput-root:hover, .MuiInputLabel-shrink:hover)': {
        '& .MuiInputLabel-shrink:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: 'transparent',
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled)': {
            backgroundColor: tokens.ColorSurfaceHover,
        },
        '& .MuiOutlinedInput-root:not(.Mui-focused):not(.Mui-disabled):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
        },
    },
    '& .MuiOutlinedInput-root': {
        ...tokens.TextS,
        backgroundColor: tokens.ColorSurfaceDefault,
        borderRadius: tokens.SizingCornerS,
        color: tokens.ColorTextBold,
        flexWrap: 'nowrap',
        height: '46px',
        padding: `0 ${tokens.SizingSpacing150}`,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDefault,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-focused': {
            backgroundColor: tokens.ColorSurfaceDefault,
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderFocused,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-error .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.ColorBorderDanger,
            borderWidth: tokens.SizingBorderS,
        },
        '&.Mui-disabled': {
            backgroundColor: tokens.ColorSurfaceDisabled,
            color: tokens.ColorTextDisabled,
        },
        '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: `${tokens.ColorBorderDisabled}!important`,
            borderWidth: tokens.SizingBorderS,
        },
    },
    '&:has(.MuiInputLabel-shrink) .MuiOutlinedInput-notchedOutline legend': {
        fontSize: tokens.TextXs.fontSize,
        maxWidth: `calc(100% - ${tokens.SizingSpacing100} - ${tokens.SizingBorderS})`,
    },
    '&[data-multiple="true"] .MuiOutlinedInput-root': {
        flexWrap: 'wrap',
        height: 'auto',
        minHeight: '46px',
        paddingBottom: tokens.SizingSpacing075,
        paddingTop: tokens.SizingSpacing075,
    },
    '& .MuiAutocomplete-hasPopupIcon .MuiOutlinedInput-root, & .MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
        paddingRight: '36px!important',
    },
    '& .MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root': {
        paddingRight: '56px!important',
    },
    '& .MuiOutlinedInput-input, & .MuiAutocomplete-inputRoot .MuiAutocomplete-input': {
        ...tokens.TextS,
        boxSizing: 'border-box',
        color: tokens.ColorTextBold,
        height: tokens.FontLineheightSm,
        minWidth: 0,
        padding: '0!important',
        textOverflow: 'ellipsis',
        '&::placeholder': {
            color: tokens.ColorTextSubtle,
            opacity: 1,
        },
        '&.Mui-disabled': {
            color: tokens.ColorTextDisabled,
            WebkitTextFillColor: tokens.ColorTextDisabled,
        },
    },
    '& .Mui-error .MuiOutlinedInput-input::placeholder': {
        color: tokens.ColorTextDangerSubtle,
    },
    '& .MuiAutocomplete-tag': {
        maxWidth: '100%',
        minWidth: 0,
    },
    '& .MuiAutocomplete-endAdornment': {
        alignItems: 'center',
        display: 'flex',
        gap: tokens.SizingSpacing050,
        right: tokens.SizingSpacing150,
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
        color: tokens.ColorIconSubtle,
        height: tokens.SizingIconS,
        margin: 0,
        padding: 0,
        width: tokens.SizingIconS,
    },
    '& .MuiAutocomplete-clearIndicator': {
        color: tokens.ColorIconBoldest,
    },
    '& .MuiAutocomplete-popupIndicator.Mui-disabled': {
        color: tokens.ColorIconDisabled,
    },
    '& .MuiFormHelperText-root': {
        ...tokens.TextXs,
        boxSizing: 'border-box',
        color: tokens.ColorTextDangerSubtle,
        height: tokens.FontLineheightXs,
        margin: `${tokens.SizingSpacing050} 0 0`,
        overflow: 'hidden',
        padding: `0 ${tokens.SizingSpacing150}`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        width: '100%',
    },
})

const AutocompletePaper = styled(Paper)({
    ...tokens.Flyout,
    backgroundColor: tokens.ColorSurfaceDefault,
    borderRadius: tokens.SizingCornerS,
    marginTop: tokens.SizingSpacing050,
    overflow: 'hidden',
    '&& .MuiAutocomplete-listbox': {
        boxSizing: 'border-box',
        maxHeight: '264px',
        padding: `${tokens.SizingSpacing050} 0`,
    },
    '&& .MuiAutocomplete-option': {
        ...tokens.TextS,
        alignItems: 'center',
        backgroundColor: tokens.ColorSurfaceDefault,
        boxSizing: 'border-box',
        color: tokens.ColorTextBold,
        display: 'flex',
        gap: tokens.SizingSpacing100,
        height: tokens.SizingSpacing400,
        minHeight: tokens.SizingSpacing400,
        overflow: 'hidden',
        padding: `0 ${tokens.SizingSpacing150}`,
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        '&.Mui-focused, &.Mui-focusVisible': {
            backgroundColor: tokens.ColorSurfaceHover,
        },
        '&:active': {
            backgroundColor: tokens.ColorSurfacePressed,
        },
        '&[aria-selected="true"]': {
            backgroundColor: tokens.ColorSurfaceSelectedDefault,
        },
        '&[aria-selected="true"].Mui-focused, &[aria-selected="true"].Mui-focusVisible': {
            backgroundColor: tokens.ColorSurfaceSelectedHover,
        },
        '&[aria-selected="true"]:active': {
            backgroundColor: tokens.ColorSurfaceSelectedPressed,
        },
        '&[aria-disabled="true"]': {
            color: tokens.ColorTextDisabled,
            opacity: 1,
        },
    },
    '&& .MuiAutocomplete-groupLabel': {
        ...tokens.TextXs,
        alignItems: 'center',
        backgroundColor: tokens.ColorSurfaceDefault,
        color: tokens.ColorTextSubtle,
        display: 'flex',
        height: tokens.SizingSpacing400,
        lineHeight: tokens.FontLineheightSm,
        padding: `0 ${tokens.SizingSpacing150}`,
        textTransform: 'uppercase',
        top: 0,
    },
    '&& .MuiAutocomplete-groupUl .MuiAutocomplete-option': {
        paddingLeft: tokens.SizingSpacing150,
    },
    '&& .MuiAutocomplete-loading, && .MuiAutocomplete-noOptions': {
        ...tokens.TextS,
        color: tokens.ColorTextSubtle,
        padding: `${tokens.SizingSpacing100} ${tokens.SizingSpacing150}`,
    },
})
